import React, { useEffect, useState, useRef } from "react";
import { Ticket, Clock, CheckCircle2, RefreshCw, ShieldCheck, Sparkles, Users } from "lucide-react";
import { pingServerHealth } from "@/lib/api";

export default function WaitingRoom() {
  const [active, setActive] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [countdown, setCountdown] = useState(48);
  const [progress, setProgress] = useState(8);
  const [queueId] = useState(() => "CGC-" + Math.floor(1000 + Math.random() * 9000));
  const [isManualChecking, setIsManualChecking] = useState(false);

  const pollIntervalRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleWarming = (e) => {
      const isWarming = e.detail?.warming;
      if (isWarming && !active && !isReady) {
        setActive(true);
        setCountdown(48);
        setProgress(12);
      } else if (!isWarming && active && !isReady) {
        // If request finished quickly, wrap up
        triggerReady();
      }
    };

    window.addEventListener("cgc:server-warming", handleWarming);
    return () => {
      window.removeEventListener("cgc:server-warming", handleWarming);
    };
  }, [active, isReady]);

  // When active, run the countdown & live server polling
  useEffect(() => {
    if (!active || isReady) return;

    // Countdown and progress bar increment
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return 1;
        return prev - 1;
      });
      setProgress((prev) => {
        if (prev >= 94) return 94; // Hold at 94% until server responds
        return prev + 1.8;
      });
    }, 1000);

    // Active heartbeat to server every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      const ok = await pingServerHealth();
      if (ok) {
        triggerReady();
      }
    }, 2000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [active, isReady]);

  const triggerReady = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    setIsReady(true);
    setProgress(100);
    setCountdown(0);

    // Dispatch event so any page that loaded empty data can re-trigger its queries
    window.dispatchEvent(new CustomEvent("cgc:server-ready"));

    // Smooth exit transition
    setTimeout(() => {
      setActive(false);
      setIsReady(false);
      setCountdown(48);
      setProgress(8);
    }, 850);
  };

  const handleManualCheck = async () => {
    setIsManualChecking(true);
    const ok = await pingServerHealth();
    setIsManualChecking(false);
    if (ok) {
      triggerReady();
    }
  };

  if (!active) return null;

  const formattedCountdown = `00:${countdown < 10 ? "0" + countdown : countdown}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-stone-950/80 backdrop-blur-md transition-opacity duration-300 animate-in fade-in"
    >
      <div className="relative w-full max-w-lg bg-stone-900 border border-stone-800 rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden text-stone-100">
        {/* Decorative ambient gradient backdrop */}
        <div className="absolute -top-24 -left-24 w-60 h-60 bg-orange-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-60 h-60 bg-amber-600/20 rounded-full blur-3xl pointer-events-none" />

        {/* Top Status Pill */}
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/10 border border-orange-500/30 text-orange-400">
            <span className="relative flex h-2 w-2">
              <span className={`absolute inline-flex h-full w-full rounded-full ${isReady ? "bg-emerald-400" : "bg-orange-400 animate-ping"} opacity-75`} />
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isReady ? "bg-emerald-500" : "bg-orange-500"}`} />
            </span>
            {isReady ? "SESSION ALLOCATED" : "HIGH DEMAND · VIRTUAL QUEUE"}
          </div>
          <span className="text-xs font-mono text-stone-400 tracking-wider">
            TOKEN: <span className="text-stone-200 font-bold">{queueId}</span>
          </span>
        </div>

        {/* Visual Pulse / Radar Indicator */}
        <div className="flex flex-col items-center text-center my-4">
          <div className="relative mb-5 flex items-center justify-center">
            {/* Concentric rings */}
            <div className={`absolute w-24 h-24 rounded-full border border-orange-500/20 ${isReady ? "scale-100 border-emerald-500/30" : "animate-ping"}`} />
            <div className={`absolute w-16 h-16 rounded-full border border-orange-500/40 ${isReady ? "border-emerald-500/50" : "animate-pulse"}`} />
            
            {/* Center Icon */}
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-colors duration-500 ${isReady ? "bg-emerald-600 text-white" : "bg-gradient-to-br from-orange-500 to-amber-600 text-white"}`}>
              {isReady ? (
                <CheckCircle2 className="w-8 h-8 animate-in zoom-in-75 duration-300" />
              ) : (
                <Ticket className="w-7 h-7" />
              )}
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
            {isReady ? "You're In! Entering Session..." : "You're in line — Holding your spot"}
          </h2>
          <p className="text-sm text-stone-300 max-w-md leading-relaxed">
            {isReady
              ? "Connection secured. Loading available tickets and reservation details now."
              : "Common Ground Collective is experiencing heavy visitor traffic. We are securing your reservation slot so you don't lose your place."}
          </p>
        </div>

        {/* Timer & Estimated Countdown Card */}
        <div className="bg-stone-950/60 border border-stone-800/80 rounded-2xl p-4 sm:p-5 my-5">
          <div className="flex items-center justify-between mb-3 text-xs">
            <span className="flex items-center gap-1.5 text-stone-400 font-medium">
              <Clock className="w-3.5 h-3.5 text-orange-400" />
              Estimated Wait
            </span>
            <span className="font-mono text-base font-bold text-orange-400">
              {isReady ? "Ready" : formattedCountdown}
            </span>
          </div>

          {/* Shimmering Progress Bar */}
          <div className="relative w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-700 rounded-full ${
                isReady
                  ? "bg-emerald-500 w-full"
                  : "bg-gradient-to-r from-orange-500 via-amber-500 to-orange-400"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center justify-between mt-2.5 text-[11px] text-stone-400">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-stone-400" />
              Position held in real-time
            </span>
            <span>{Math.round(progress)}% allocated</span>
          </div>
        </div>

        {/* Trust & Queue Information */}
        <div className="grid grid-cols-2 gap-2 text-xs text-stone-400 mb-6">
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-stone-800/40 border border-stone-800/50">
            <Users className="w-4 h-4 text-orange-400 shrink-0" />
            <div>
              <div className="text-[10px] text-stone-500 uppercase font-semibold">Priority</div>
              <div className="text-stone-300 font-medium">Verified Visitor</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-stone-800/40 border border-stone-800/50">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
            <div>
              <div className="text-[10px] text-stone-500 uppercase font-semibold">Server State</div>
              <div className="text-stone-300 font-medium">{isReady ? "Online" : "Waking up..."}</div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-stone-800 text-xs">
          <div className="text-stone-400 text-center sm:text-left text-[11px]">
            Please don't refresh this page. You will be redirected automatically.
          </div>
          <button
            type="button"
            onClick={handleManualCheck}
            disabled={isManualChecking || isReady}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 transition-colors font-medium text-xs disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`w-3 h-3 ${isManualChecking ? "animate-spin" : ""}`} />
            {isManualChecking ? "Checking..." : "Check Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
